import * as React from 'react';
import MyTypes from 'MyTypes';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { RouteComponentProps } from 'react-router';
import { Accordion, Badge, Card, Form, Row, Col, Button, Spinner, Table, Modal } from 'react-bootstrap'; 
import * as models from '../../models'; 
import { companyActions } from 'store/companies'; 
import { locationActions } from 'store/locations';
import { userActions } from 'store/user';
import { USER_ACTIONS } from 'store/enums';
import _ from 'lodash'; 
import { userHasPermission } from '../../utils/helpers';
import { PhoneNumberType } from 'models/contacts/phoneNumber';
import { LuLink } from 'react-icons/lu';
import { FaFileAlt, FaCloudUploadAlt, FaEye } from 'react-icons/fa';
import * as router from '../../services/router-service'; 
import { AppToastTypes } from 'models/enums'; 
import { contactsActions } from 'store/contacts';
import downloadIcon from '../../assets/images/Blue/download.svg';
import deleteIcon from '../../assets/images/Blue/trash.svg';
import SingleActionButton from 'components/common/SingleActionButton';
import CompanyFileUploadModal from './CompanyFileUploadModal';
import dateFormat from 'dateformat';
import services from 'services';
import utils from 'utils';

interface Props extends PropsFromRedux {}

interface State {
    company: models.Company; 
    isUpdated: boolean;
    timer: ReturnType<typeof setTimeout>; 
    showUploadModal: boolean;
    // File Viewer Modal State
    showViewerModal: boolean;
    viewerFileName: string;
    viewerFileType: 'text' | 'pdf' | 'unsupported';
    viewerContent: string;
    isViewerLoading: boolean;
}

interface IReactRouterParams {
    companyId?: string; 
}

class CompanyDetail extends React.Component<Props & RouteComponentProps<IReactRouterParams>, State> {    
    public state: State = {
        company: models.defaultCompany, 
        isUpdated: false, 
        timer: setTimeout(function(){}, 1000),
        showUploadModal: false,
        showViewerModal: false,
        viewerFileName: '',
        viewerFileType: 'text',
        viewerContent: '',
        isViewerLoading: false
    }

    public componentDidMount() {
        if(this.props.match.params.companyId !== '0')
            this.props.requestCompany(parseInt(this.props.match.params.companyId!)); 
        this.props.requestFieldDirectorsDD(); 
    }

    componentDidUpdate(prevProps: any, prevState: any) {
        if (!_.isEqual(prevProps.company, this.props.company)) {            
            this.setState({company: {...this.props.company}}); 
        }

        if (!_.isEqual(prevProps.zipSearch, this.props.zipSearch)) { 
            let company: models.Company = {
                ...this.state.company,
                mailingAddress: {
                    ...this.state.company.mailingAddress,
                    city: this.props.zipSearch.cityName,
                    state: this.props.zipSearch.stateName,
                    postalCode: this.props.zipSearch.zipCode
                }
            };
            this.setState({company: company}); 
        }

        if(!_.isEqual(prevState.company.mailingAddress?.postalCode, this.state.company.mailingAddress?.postalCode)) {
            this.handleTimelapse(); 
        }

        if(!_.isEqual(prevProps.lastUserAction, this.props.lastUserAction) && this.props.lastUserAction === USER_ACTIONS.COMPANY_UPDATE_SUCCESS) {
            this.setState({isUpdated: false}); 
        }        
    }

    handleTimelapse = () => {        
        clearTimeout(this.state.timer); 
        const zipCode = this.state.company.mailingAddress?.postalCode; 

        if(zipCode && zipCode !== '')
            this.setState({timer: setTimeout(() => {this.props.requestZipSearch(zipCode)}, 1000)});
    }

    public submitForm = (e: any) => {
        e.preventDefault();        
        const { company } = this.state;
        
        if(!company.name){
            this.props.setAppToast({message: "Company Name Required", type: AppToastTypes.Failure });            
            return;
        }

        const sanitizePhones = (comp: models.Company) => {
            const copy = _.cloneDeep(comp);
            if (copy.phoneNumbers) {
                copy.phoneNumbers.forEach(p => {
                    if (p.phoneNumber) p.phoneNumber = p.phoneNumber.replace(/\D/g, '');
                });
            }
            return copy;
        };
        const cleanState = sanitizePhones(company);
        try {
            if(company.companyId === 0) {
                const createRequest: models.CreateCompanyRequest = {
                    name: cleanState.name,
                    type: cleanState.type,
                    email: cleanState.email,
                    claimEmail: cleanState.claimEmail,
                    phoneNumbers: cleanState.phoneNumbers,
                    mailingAddress: cleanState.mailingAddress,
                    assignedRep: cleanState.assignedRep,
                    xaIsBusinessUnit: cleanState.xaIsBusinessUnit,
                    xaCompanyName: cleanState.xaCompanyName
                }; 
                this.props.createCompany(createRequest); 
            } else {
                this.props.updateCompany(company.companyId, cleanState);
            }            
        } catch (error) {
            console.error("Error saving company", error);
        }
    }

    private formatPhoneNumber = (value: string) => {
        if (!value) return value;
        const phoneNumber = value.replace(/[^\d]/g, '');
        const phoneNumberLength = phoneNumber.length;

        if (phoneNumberLength < 4) return phoneNumber;
        if (phoneNumberLength < 7) {
            return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
        }
        return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    public handlePhoneNumberChange = (e: any) => {        
        const inputItem = e.target.name; 
        let rawValue = e.target.value.replace(/\D/g, ''); 
        
        if (rawValue.length > 10) {
            rawValue = rawValue.substring(0, 10);
        }

        const targetType = inputItem as PhoneNumberType; 
        const currentNumbers = this.state.company.phoneNumbers || [];
        let phoneNumbers: models.PhoneNumber[]; 

        if (rawValue !== "") {
            phoneNumbers = currentNumbers
                .filter(x => x.phoneNumberType !== targetType)
                .concat({ phoneNumberType: targetType, phoneNumber: rawValue }); 
        }       
        else {
            phoneNumbers = currentNumbers.filter(x => x.phoneNumberType !== targetType);
        }        
        
        this.setState({
            company: {
                ...this.state.company, 
                phoneNumbers: phoneNumbers
            }, isUpdated: true
        }); 
    }

    public handleDeleteFile = (fileId: number) => {
        const { company } = this.state;
        
        if (window.confirm("Are you sure you want to permanently delete this reference file?")) {     
            this.props.deleteCompanyFile(fileId, company.companyId);
        }
    }

    downloadFile = async (id: number) => {
        const response = await services.api.company.getCompanyFile(this.props.company.companyId, id); 
        if(response) {
            utils.filehelpers.downloadFile(response); 
        }
    }

    // View file handler for .md, .txt, .pdf, and fallback
    viewFile = async (file: models.CompanyFile) => {
        const ext = file.fileName.substring(file.fileName.lastIndexOf('.')).toLowerCase();
        
        this.setState({
            showViewerModal: true,
            viewerFileName: file.fileName,
            isViewerLoading: true,
            viewerContent: '',
            viewerFileType: (ext === '.pdf') ? 'pdf' : (['.md', '.txt', '.csv', '.json', '.html', '.htm'].includes(ext) ? 'text' : 'unsupported')
        });

        if (ext === '.docx' || ext === '.xlsx' || ext === '.doc') {
            this.setState({ isViewerLoading: false });
            return;
        }

        try {
            const response = await services.api.company.getCompanyFile(this.props.company.companyId, file.id);
            if (response && response.data) {
                if (ext === '.pdf') {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const blobUrl = URL.createObjectURL(blob);
                    this.setState({ viewerContent: blobUrl, isViewerLoading: false });
                } else {
                    // For text/md files
                    const text = typeof response.data === 'string' 
                        ? response.data 
                        : await new Response(response.data).text();
                    this.setState({ viewerContent: text, isViewerLoading: false });
                }
            }
        } catch (err) {
            console.error("Failed to load file content for preview", err);
            this.setState({ 
                viewerContent: "Failed to load document content for preview.", 
                isViewerLoading: false 
            });
        }
    }

    closeViewerModal = () => {
        if (this.state.viewerFileType === 'pdf' && this.state.viewerContent) {
            URL.revokeObjectURL(this.state.viewerContent);
        }
        this.setState({ showViewerModal: false, viewerContent: '', viewerFileName: '' });
    }

    public handleBack = () => { router.goto(router.url.companies); }

    public render() {
        const { fieldDirectorsDD, isCompanyLoading, isStateAbbrDDLoading, stateResponse } = this.props; 
        const { company, isUpdated, showViewerModal, viewerFileName, viewerFileType, viewerContent, isViewerLoading } = this.state; 
        const canEdit = userHasPermission("Manage Companies"); 

        return (  
            <>            
            <Form onSubmit={this.submitForm}>
                {/* --- Top Action Bar --- */}
                <Row className="mb-3 d-flex align-items-center justify-content-between">
                     <Col xs="auto" className="d-flex gap-2">
                     </Col>
                     <Col xs="auto">
                        <Button className="submit-contact-btn me-2" onClick={this.handleBack}>BACK</Button>
                        {canEdit && <Button className="submit-contact-btn" type="submit" disabled={!isUpdated}>SUBMIT</Button>}
                     </Col>
                </Row>

                {/* --- XactAnalysis Integration --- */}
                <Accordion className="mb-3 shadow-sm" defaultActiveKey={[]}>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <div className="d-flex align-items-center w-100">
                                <LuLink className="w-5 h-5 me-2 text-primary" />
                                <strong>XactAnalysis Integration</strong>
                                <div className="ms-auto me-3">
                                    <Badge bg={(company.xaCompanyName && company.isActive) ? "success" : "secondary"}>
                                        {(company.xaCompanyName && company.isActive) ? "Enabled" : "Disabled"}
                                    </Badge>
                                </div>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            <Row className="align-items-center">
                                <Col md={4}>
                                    <Form.Group id="formIsBusinessUnit">
                                        <Form.Check 
                                            type="checkbox"
                                            id="is-business-unit-checkbox"
                                            label="Is Business Unit"
                                            checked={company.xaIsBusinessUnit || false}
                                            onChange={(e) => this.setState({
                                                company: { ...company, xaIsBusinessUnit: e.target.checked }, 
                                                isUpdated: true
                                            })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={8}>
                                    <Form.Group controlId="formXaCompanyName">
                                        <Form.Label className="small text-uppercase fw-bold text-muted">Xact Company Name</Form.Label>
                                        <Form.Control 
                                            type="text" 
                                            placeholder="Enter Exact Name match from XactAnalysis..." 
                                            value={company.xaCompanyName || ""} 
                                            onChange={(e) => this.setState({
                                                company: { ...company, xaCompanyName: e.target.value }, 
                                                isUpdated: true
                                            })} 
                                        />
                                        <Form.Text className="text-muted">
                                            {company.xaIsBusinessUnit 
                                                ? "The value entered must exactly match the Business Unit value in the Xact assignment import." 
                                                : "The value entered must exactly match the Carrier Name value in the Xact assignment import."}
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                {/* --- Company Info Card with Stats --- */}
                <Card className="claim-card">
                    <Card.Title className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            Company Info
                            {isCompanyLoading && <Spinner animation="border" role="status" variant="primary" size="sm" className="ms-2"/>}
                        </div>
                        <div className="d-flex gap-2" style={{fontSize: '0.85rem'}}>
                            <Badge bg="primary" className="d-flex align-items-center px-2 py-1 shadow-sm">
                                <span className="fw-bold me-1">{(company.activeClaimCount || 0).toLocaleString()}</span>
                                <span className="fw-normal">Active</span>
                            </Badge>
                            <Badge bg="info" text="dark" className="d-flex align-items-center px-2 py-1 shadow-sm" title="Opened This Year">
                                <span className="fw-bold me-1">{(company.ytdOpenClaims || 0).toLocaleString()}</span>
                                <span className="fw-normal">YTD Open</span>
                            </Badge>
                            <Badge bg="success" className="d-flex align-items-center px-2 py-1 shadow-sm" title="Closed This Year">
                                <span className="fw-bold me-1">{(company.ytdClosedClaims || 0).toLocaleString()}</span>
                                <span className="fw-normal">YTD Closed</span>
                            </Badge>
                            <Badge bg="secondary" className="d-flex align-items-center px-2 py-1 shadow-sm">
                                <span className="fw-bold me-1">{(company.lifetimeClaimCount || 0).toLocaleString()}</span>
                                <span className="fw-normal">Lifetime</span>
                            </Badge>
                            <Badge bg="light" text="dark" className="border d-flex align-items-center px-2 py-1">
                                <span className="fw-normal text-muted me-1">Last:</span>
                                <span className="fw-bold">
                                    {company.lastClaimDate ? new Date(company.lastClaimDate).toLocaleDateString() : 'N/A'}
                                </span>
                            </Badge>
                        </div>
                    </Card.Title>                    
                    <Card.Body>
                        <Row className="mb-3">
                            <Form.Group as={Col} id="formIsActive">
                                <Form.Check 
                                    disabled={!canEdit || company.companyId === 0}
                                    type="checkbox" 
                                    label="Active Company" 
                                    checked={company.isActive || company.companyId === 0}                                    
                                    onChange={(e) => this.setState({company: {...company, isActive: !company.isActive}, isUpdated: true})}
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col} controlId="formCompanyName">
                                <Form.Label>Company Name</Form.Label>
                                <Form.Control 
                                    disabled={!canEdit}
                                    type="text" 
                                    placeholder="Enter Company Name..." 
                                    value={company.name || ""} 
                                    onChange={(e) => this.setState({company: {...company, name: e.target.value}, isUpdated: true})} 
                                />
                            </Form.Group>
                            <Form.Group as={Col} controlId="formEmail">
                                <Form.Label>Company Email</Form.Label>
                                <Form.Control 
                                    disabled={!canEdit}
                                    type="email" 
                                    placeholder="Enter Company Email Address..." 
                                    value={company.email || ""} 
                                    onChange={(e) => this.setState({company: {...company, email: e.target.value}, isUpdated: true})} 
                                />
                            </Form.Group>
                            <Form.Group as={Col} controlId="formEmail2">
                                <Form.Label>Claim Email</Form.Label>
                                <Form.Control 
                                    disabled={!canEdit}
                                    type="email" 
                                    placeholder="Enter Claim Email Address..." 
                                    value={company.claimEmail || ""} 
                                    onChange={(e) => this.setState({company: {...company, claimEmail: e.target.value}, isUpdated: true})} 
                                />
                            </Form.Group>
                            <Form.Group as={Col} controlId="formAssignedRep">
                                <Form.Label>Assigned Rep</Form.Label>
                                <Form.Select 
                                    disabled={!canEdit}
                                    value={company.assignedRep?.id || ""} 
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        const newRep = val 
                                            ? { id: parseInt(val, 10), name: e.target.options[e.target.selectedIndex].text } 
                                            : undefined;
                                        
                                        this.setState({
                                            company: { ...company, assignedRep: newRep as any }, 
                                            isUpdated: true
                                        });
                                    }} 
                                >
                                    <option value="">Unassigned</option>
                                    {fieldDirectorsDD && fieldDirectorsDD.resourceList?.map((rep: models.DropDownListItem) => (
                                        <option key={rep.id} value={rep.id}>{rep.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Label>Company Type</Form.Label>
                            <Form.Group className="checkbox-group-hor" >
                                {["Adjuster", "Agent", "Independent Adjuster", "Insurance", "Mortgage"].map(type => (
                                    <Form.Check 
                                        key={type}
                                        disabled={!canEdit}
                                        inline={true}
                                        name={type}
                                        type="radio"
                                        label={type}
                                        id={`chk${type.replace(/\s/g, '')}`}
                                        checked={company.type === type}
                                        onChange={(e) => this.setState({company: {...company, type: e.target.name}, isUpdated: true})} 
                                    />
                                ))}
                            </Form.Group>
                        </Row>
                    </Card.Body>
                </Card>                    
                
                <Card className="claim-card">
                    <Card.Title>Contact Numbers</Card.Title>
                    <Card.Body>
                        <Row className="mb-3">
                            <Form.Group as={Col} controlId="formBusiness">
                                <Form.Label>Business</Form.Label>
                                <Form.Control 
                                    disabled={!canEdit}
                                    name="Business"
                                    type="text" 
                                    placeholder="Enter Business #..." 
                                    value={this.formatPhoneNumber(company.phoneNumbers?.find(p => p?.phoneNumberType == PhoneNumberType.Business)?.phoneNumber || '')}
                                    onChange={this.handlePhoneNumberChange} 
                                />
                            </Form.Group>
                            <Form.Group as={Col} controlId="formFax">
                                <Form.Label>Fax</Form.Label>
                                <Form.Control 
                                    disabled={!canEdit}
                                    name="Fax"
                                    type="text" 
                                    placeholder="Enter Fax #..." 
                                    value={this.formatPhoneNumber(company.phoneNumbers?.find(p => p?.phoneNumberType == PhoneNumberType.Fax)?.phoneNumber || '')}
                                    onChange={this.handlePhoneNumberChange} 
                                />
                            </Form.Group>
                            <Form.Group as={Col} controlId="formOther">
                                <Form.Label>Other</Form.Label>
                                <Form.Control 
                                    disabled={!canEdit}
                                    name="Other"
                                    type="text" 
                                    placeholder="Enter Other #..." 
                                    value={this.formatPhoneNumber(company.phoneNumbers?.find(p => p?.phoneNumberType == PhoneNumberType.Other)?.phoneNumber || '')}
                                    onChange={this.handlePhoneNumberChange} 
                                />
                            </Form.Group>
                        </Row>
                    </Card.Body>
                </Card>

                <Card className="claim-card">
                    <Card.Title>Mailing Address</Card.Title>
                    <Card.Body>
                        <Row className="mb-3">
                            <Form.Group as={Col} controlId="formAddress1">
                                <Form.Label>Address</Form.Label>
                                <Form.Control 
                                    disabled={!canEdit}
                                    placeholder="1234 Main St" 
                                    value={company.mailingAddress?.addressLine1 || ""} 
                                    onChange={(e) => this.setState({company: {...company, mailingAddress: {...company.mailingAddress, addressLine1: e.target.value}}, isUpdated: true})} 
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col} controlId="formAddress2">
                                <Form.Label>Address 2</Form.Label>
                                <Form.Control 
                                    disabled={!canEdit}
                                    placeholder="Apartment, studio, or floor" 
                                    value={company.mailingAddress?.addressLine2 || ""} 
                                    onChange={(e) => this.setState({company: {...company, mailingAddress: {...company.mailingAddress, addressLine2: e.target.value}}, isUpdated: true})} 
                                />
                            </Form.Group>
                        </Row>

                        <Row className="mb-3">
                            <Form.Group as={Col} controlId="formCity">
                                <Form.Label>City</Form.Label>
                                <Form.Control 
                                    disabled={!canEdit}
                                    placeholder='Enter City...' 
                                    value={company.mailingAddress?.city || ""} 
                                    onChange={(e) => this.setState({company: {...company, mailingAddress: {...company.mailingAddress, city: e.target.value}}, isUpdated: true})} 
                                />
                            </Form.Group>

                            <Form.Group as={Col} controlId="formState">
                                <Form.Label>State</Form.Label>
                                <Form.Select 
                                    disabled={!canEdit}
                                    name="states"  
                                    value={company.mailingAddress?.state || ""} 
                                    onChange={(e) => this.setState({company: {...company, mailingAddress: {...company.mailingAddress, postalCode: "", city: "", state: e.target.value}}, isUpdated: true})} 
                                >
                                    {isStateAbbrDDLoading && <option>Loading...</option>}
                                    {!isStateAbbrDDLoading && <option key={-1}>Choose...</option>}
                                    {stateResponse && stateResponse.resourceList?.map((s: models.DropDownListItem, i: number) => ( 
                                        <option key={i} value={s.name} >{s.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group as={Col} controlId="formZip">
                                <Form.Label>Zip</Form.Label>
                                <Form.Control 
                                    disabled={!canEdit}
                                    placeholder='Enter Zip...' 
                                    value={company.mailingAddress?.postalCode || ""} 
                                    onChange={(e) => this.setState({company: {...company, mailingAddress: {...company.mailingAddress, postalCode: e.target.value}}, isUpdated: true})} 
                                />
                            </Form.Group>
                        </Row>
                    </Card.Body>
                </Card>

                {/* --- Company Reference Files (AI Lab Knowledge Base) --- */}
                {company.companyId > 0 && (
                    <Card className="claim-card mt-3">
                        <Card.Title className="d-flex align-items-center justify-content-between">
                            <div>
                                <FaFileAlt className="me-2 text-primary" />
                                Company Reference Files
                            </div>
                            <Button 
                                className="d-flex"
                                style={{color: "#2259a1"}}
                                onClick={() => this.setState({ showUploadModal: true })}
                            >
                                <FaCloudUploadAlt className="me-2" style={{ fontSize: '1.2rem' }} /> 
                                UPLOAD NEW FILE
                            </Button>
                        </Card.Title>
                        <Card.Body>
                            {company.companyFiles && company.companyFiles.length > 0 ? (
                                <Table striped hover responsive className="align-middle">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>File Name</th>
                                            <th>Description</th>
                                            <th>Uploaded</th>
                                            <th>Is Active</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {company.companyFiles.map((file: models.CompanyFile) => (
                                            <tr key={file.id}>
                                                <td>
                                                    <Badge bg="info" text="dark">{file.fileTypeName}</Badge>
                                                </td>
                                                <td>
                                                    <div className="fw-bold text-primary">{file.fileName}</div>
                                                    <small className="text-muted">{file.createdBy}</small>
                                                </td>
                                                <td className="small text-muted">{file.description}</td>
                                                <td className="small">{dateFormat(file.createdDate, "mm/dd/yyyy")}</td>
                                                <td>{file.isActive ? 'Yes' : 'No'}</td>
                                                <td className="text-end">
                                                    <div className="d-flex justify-content-end gap-2 align-items-center">
                                                        <Button 
                                                            variant="link" 
                                                            className="p-0 text-primary" 
                                                            title="View File" 
                                                            onClick={() => this.viewFile(file)}
                                                        >
                                                            <FaEye style={{ fontSize: '1.2rem' }} />
                                                        </Button>

                                                        <SingleActionButton
                                                            action="Download"
                                                            title="File"
                                                            img={downloadIcon}
                                                            onClick={() => this.downloadFile(file.id)}
                                                        />
                                                        <SingleActionButton
                                                            action="Delete"
                                                            title={`${file.fileName}`} 
                                                            img={deleteIcon}
                                                            onClick={() => this.handleDeleteFile(file.id)}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center py-4 text-muted border rounded bg-light">
                                    <p className="mb-0 small">No reference files uploaded for this carrier.</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                )}
            </Form>

            {/* --- Upload Modal --- */}
            <CompanyFileUploadModal
                show={this.state.showUploadModal}
                companyId={company.companyId}
                closeModal={() => this.setState({ showUploadModal: false })}
            />

            {/* --- Document Preview Modal --- */}
            <Modal 
                show={showViewerModal} 
                onHide={this.closeViewerModal} 
                size="xl" 
                centered 
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title className="h6 mb-0 d-flex align-items-center">
                        <FaFileAlt className="me-2 text-primary" />
                        Viewing: {viewerFileName}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ minHeight: '60vh', maxHeight: '75vh' }}>
                    {isViewerLoading ? (
                        <div className="d-flex justify-content-center align-items-center h-100 py-5">
                            <Spinner animation="border" variant="primary" />
                            <span className="ms-2">Loading document content...</span>
                        </div>
                    ) : (
                        <>
                            {viewerFileType === 'text' && (
                                <pre style={{ 
                                    whiteSpace: 'pre-wrap', 
                                    fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.5,
                                    margin: 0,
                                    padding: '1rem',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '4px'
                                }}>
                                    {viewerContent}
                                </pre>
                            )}

                            {viewerFileType === 'pdf' && (
                                <iframe 
                                    src={viewerContent} 
                                    title={viewerFileName} 
                                    width="100%" 
                                    height="600px" 
                                    style={{ border: 'none' }}
                                />
                            )}

                            {viewerFileType === 'unsupported' && (
                                <div className="text-center py-5">
                                    <p className="text-muted mb-2">In-browser preview is not supported for this file type.</p>
                                    <p className="small text-muted">Please download the file to view its contents.</p>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.closeViewerModal}>Close</Button>
                </Modal.Footer>
            </Modal>
            </>
        ); 
    }
}

const mapStateToProps = (state: MyTypes.RootState) => ({
    company: state.companies.company.company, 
    fieldDirectorsDD: state.contacts.dropdowns.fieldDirectorsDD,
    isCompanyLoading: state.companies.company.isCompanyLoading,    
    isStateAbbrDDLoading: state.locations.dropdowns.isStateDDLoading, 
    lastUserAction: state.user.actions.lastUserAction,
    stateResponse: state.locations.dropdowns.stateDD,  
    zipSearch: state.locations.locations.zipSearch
});

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {            
            createCompany: companyActions.createCompany,
            deleteCompanyFile: companyActions.deleteCompanyFile,
            requestCompany: companyActions.requestCompany, 
            requestFieldDirectorsDD: contactsActions.requestFieldDirectorsDD,
            requestZipSearch: locationActions.requestZipSearch,
            updateCompany: companyActions.updateCompany,   
            setAppToast: userActions.setAppToast,         
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps); 
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(CompanyDetail);